import { useAppDispatch, useAppSelector } from "../../redux";
import { Icon } from "../base/Icon";
import { DropdownField, Text, View } from "../base";
import { OrganizationTelephony, organizationTelephonyActions } from "../../redux/reducers/organizationTelephonySlice";
import { useSbcRegions } from "../../hooks/useSbcRegions";
import { useEffect } from "react";

type TelephonyConfigurationDropdownProps = {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  organizationId?: string;
};

export const TelephonyConfigurationDropdown = ({
  value,
  onChange,
  label = "Telephony Configuration",
  placeholder = "Select a telephony configuration",
  readOnly = false,
  className,
  organizationId,
}: TelephonyConfigurationDropdownProps) => {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector((state) => state.session.user?.isAdmin);
  // Platform (VoiceRun-owned) SBC regions — admin-only. Fetched only when admin
  // to avoid a guaranteed 403 from the admin-gated endpoint.
  const { regions: sbcRegions } = useSbcRegions(!!isAdmin);
  const telephonyConfigs = useAppSelector((state) =>
    organizationId ? Object.values(state.organizationTelephony.entities).filter((config) => config.organizationId === organizationId) : [],
  );

  const telephonyOptions = [
    {
      value: "",
      label: (
        <View className="flex items-center gap-2">
          <Icon
            name="voicerun"
            size={16}
            className="text-gray-700 dark:text-gray-300"
          />
          <Text className="text-gray-700 dark:text-gray-300">VoiceRun</Text>
        </View>
      ),
    },
    ...(isAdmin
      ? [
          {
            value: "mock-voicerun",
            label: (
              <View className="flex items-center gap-2">
                <Icon
                  name="mock-voicerun"
                  size={16}
                  className="text-gray-700 dark:text-gray-300"
                />
                <Text className="text-gray-700 dark:text-gray-300">Mock VoiceRun</Text>
              </View>
            ),
          },
        ]
      : []),
    ...(isAdmin
      ? sbcRegions.map((region) => ({
          value: region.telephonyId,
          label: (
            <View className="flex items-center gap-2">
              <Icon
                name="voicerun-sbc"
                size={16}
                className="text-gray-700 dark:text-gray-300"
              />
              <Text className="text-gray-700 dark:text-gray-300">{region.label}</Text>
            </View>
          ),
        }))
      : []),
    ...telephonyConfigs.map((config: OrganizationTelephony) => ({
      value: config.id,
      label: (
        <View className="flex items-center gap-2">
          {config.providerType === "twilio" && (
            <Icon
              name="twilio"
              size={16}
              className="text-gray-700 dark:text-gray-300"
            />
          )}
          {config.providerType === "telnyx" && (
            <Icon
              name="telnyx"
              size={16}
              className="text-gray-700 dark:text-gray-300"
            />
          )}
          <Text className="text-gray-700 dark:text-gray-300">{config.name}</Text>
        </View>
      ),
    })),
  ];

  useEffect(() => {
    if (organizationId) {
      dispatch(organizationTelephonyActions.get({}));
    }
  }, [organizationId]);

  return (
    <View className={className}>
      <DropdownField
        name="telephonyId"
        label={label}
        placeholder={placeholder}
        options={telephonyOptions}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
    </View>
  );
};
